import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import api from '../../utils/api';

const EMPTY_FORM = {
  name: '', basic: '', hra: '', conveyance: '', special_allowance: '', other_allowance: '',
  pf_percent: '12', esic_percent: '0.75', professional_tax: '200', tds_percent: '0',
};

function fmt(n) { return '₹' + Number(n || 0).toLocaleString('en-IN'); }

export default function SalaryPage() {
  const qc = useQueryClient();
  const [showModal, setShowModal] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);

  const { data: structures = [], isLoading } = useQuery({
    queryKey: ['hr-salary'],
    queryFn: () => api.get('/hr/salary').then(r => r.data),
  });

  const saveMutation = useMutation({
    mutationFn: data => editId ? api.patch(`/hr/salary/${editId}`, data) : api.post('/hr/salary', data),
    onSuccess: () => {
      qc.invalidateQueries(['hr-salary']);
      toast.success(editId ? 'Structure updated' : 'Structure created');
      setShowModal(false);
    },
    onError: err => toast.error(err.response?.data?.error || 'Failed'),
  });

  const deleteMutation = useMutation({
    mutationFn: id => api.delete(`/hr/salary/${id}`),
    onSuccess: () => { qc.invalidateQueries(['hr-salary']); toast.success('Deactivated'); },
    onError: err => toast.error(err.response?.data?.error || 'Failed'),
  });

  function openAdd() { setEditId(null); setForm(EMPTY_FORM); setShowModal(true); }
  function openEdit(s) {
    setEditId(s.id);
    setForm({
      name: s.name, basic: s.basic, hra: s.hra, conveyance: s.conveyance,
      special_allowance: s.special_allowance, other_allowance: s.other_allowance,
      pf_percent: s.pf_percent, esic_percent: s.esic_percent,
      professional_tax: s.professional_tax, tds_percent: s.tds_percent,
    });
    setShowModal(true);
  }
  function set(k, v) { setForm(f => ({ ...f, [k]: v })); }

  const gross = [form.basic, form.hra, form.conveyance, form.special_allowance, form.other_allowance].reduce((a, b) => a + (parseFloat(b) || 0), 0);
  const pfDed = Math.round((parseFloat(form.basic) || 0) * (parseFloat(form.pf_percent) || 0) / 100);
  const esicDed = Math.round(gross * (parseFloat(form.esic_percent) || 0) / 100);
  const profTax = parseFloat(form.professional_tax) || 0;
  const tdsDed = Math.round(gross * (parseFloat(form.tds_percent) || 0) / 100);
  const totalDed = pfDed + esicDed + profTax + tdsDed;
  const net = gross - totalDed;

  if (isLoading) return <div className="loading-screen"><div className="spinner" /></div>;

  return (
    <div>
      <div className="section-head">
        <h2>Salary Structures</h2>
        <button className="btn-primary" onClick={openAdd}>+ New Structure</button>
      </div>

      {structures.length === 0 ? (
        <div className="empty card">No salary structures yet. Create one to assign to employees.</div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(300px,1fr))', gap: 16 }}>
          {structures.map(s => {
            const g = [s.basic, s.hra, s.conveyance, s.special_allowance, s.other_allowance].reduce((a, b) => a + Number(b), 0);
            const pf = Math.round(Number(s.basic) * Number(s.pf_percent) / 100);
            const esic = Math.round(g * Number(s.esic_percent) / 100);
            const deductions = pf + esic + Number(s.professional_tax);
            return (
              <div key={s.id} className="card">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 15 }}>{s.name}</div>
                    <div style={{ fontSize: 11, color: 'var(--text2)', marginTop: 2 }}>{s.employee_count || 0} employee{s.employee_count !== 1 ? 's' : ''}</div>
                  </div>
                  <div style={{ display: 'flex', gap: 4 }}>
                    <button className="btn btn-sm" onClick={() => openEdit(s)}>Edit</button>
                    {!Number(s.employee_count) && <button className="btn-danger btn-sm" onClick={() => { if (window.confirm('Deactivate this structure?')) deleteMutation.mutate(s.id); }}>✕</button>}
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, marginBottom: 12 }}>
                  {[['Basic', s.basic], ['HRA', s.hra], ['Conveyance', s.conveyance], ['Special Allow.', s.special_allowance]].map(([label, val]) => (
                    <div key={label} style={{ fontSize: 12 }}>
                      <span style={{ color: 'var(--text2)' }}>{label}: </span>
                      <span style={{ fontWeight: 600 }}>{fmt(val)}</span>
                    </div>
                  ))}
                </div>

                <hr className="divider" />

                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: 'var(--text2)', marginBottom: 4 }}>
                  <span>Gross CTC</span><span style={{ fontWeight: 700, color: 'var(--text)' }}>{fmt(g)}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: 'var(--text2)', marginBottom: 4 }}>
                  <span>Total Deductions</span><span style={{ color: 'var(--red)' }}>– {fmt(deductions)}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, fontWeight: 700 }}>
                  <span>Net Take Home</span><span style={{ color: 'var(--accent)' }}>{fmt(g - deductions)}</span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showModal && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: 600 }}>
            <div className="modal-title">{editId ? 'Edit Salary Structure' : 'New Salary Structure'}</div>
            <form onSubmit={e => { e.preventDefault(); saveMutation.mutate(form); }}>
              <div className="field">
                <label>Structure Name *</label>
                <input value={form.name} onChange={e => set('name', e.target.value)} placeholder="e.g. Graphic Designer — 25K" required />
              </div>

              <div style={{ fontWeight: 700, fontSize: 12, color: 'var(--text2)', marginBottom: 8, marginTop: 4, textTransform: 'uppercase', letterSpacing: '.04em' }}>Earnings</div>
              <div className="form-grid">
                {[['basic','Basic Salary'], ['hra','HRA'], ['conveyance','Conveyance'], ['special_allowance','Special Allowance'], ['other_allowance','Other Allowance']].map(([k, label]) => (
                  <div key={k} className="field">
                    <label>{label}</label>
                    <input type="number" min="0" value={form[k]} onChange={e => set(k, e.target.value)} placeholder="0" />
                  </div>
                ))}
              </div>

              <div style={{ fontWeight: 700, fontSize: 12, color: 'var(--text2)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '.04em' }}>Deductions</div>
              <div className="form-grid">
                <div className="field"><label>PF % (Employee)</label><input type="number" step="0.01" value={form.pf_percent} onChange={e => set('pf_percent', e.target.value)} /></div>
                <div className="field"><label>ESIC %</label><input type="number" step="0.01" value={form.esic_percent} onChange={e => set('esic_percent', e.target.value)} /></div>
                <div className="field"><label>Professional Tax (₹/mo)</label><input type="number" value={form.professional_tax} onChange={e => set('professional_tax', e.target.value)} /></div>
                <div className="field"><label>TDS %</label><input type="number" step="0.01" value={form.tds_percent} onChange={e => set('tds_percent', e.target.value)} /></div>
              </div>

              {gross > 0 && (
                <div className="card" style={{ background: 'var(--accent-light)', marginBottom: 0 }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, fontSize: 12 }}>
                    <div><label style={{ fontSize: 10 }}>Gross CTC</label><div style={{ fontWeight: 700 }}>{fmt(gross)}</div></div>
                    <div><label style={{ fontSize: 10 }}>Total Deductions</label><div style={{ color: 'var(--red)' }}>– {fmt(totalDed)}</div></div>
                    <div><label style={{ fontSize: 10 }}>Net Take Home</label><div style={{ fontWeight: 700, color: 'var(--accent)' }}>{fmt(net)}</div></div>
                  </div>
                </div>
              )}

              <div className="modal-actions">
                <button type="button" onClick={() => setShowModal(false)}>Cancel</button>
                <button type="submit" className="btn-primary" disabled={saveMutation.isPending}>
                  {saveMutation.isPending ? 'Saving…' : editId ? 'Save Changes' : 'Create Structure'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
